import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Plus, Trash2, RefreshCw, X } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID;
const FACEBOOK_GRAPH_VERSION =
  process.env.REACT_APP_FACEBOOK_GRAPH_VERSION || process.env.REACT_APP_GRAPH_VERSION;

let facebookSdkPromise = null;

const loadFacebookSdk = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Facebook SDK is unavailable.'));
  }

  if (window.FB) {
    return Promise.resolve(window.FB);
  }

  if (!FACEBOOK_APP_ID || !FACEBOOK_GRAPH_VERSION) {
    return Promise.reject(new Error('Facebook App ID or Graph version is not configured.'));
  }

  if (facebookSdkPromise) {
    return facebookSdkPromise;
  }

  facebookSdkPromise = new Promise((resolve, reject) => {
    const existingInit = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      if (typeof existingInit === 'function') {
        existingInit();
      }
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: FACEBOOK_GRAPH_VERSION
      });
      resolve(window.FB);
    };

    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.async = true;
      script.defer = true;
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.onerror = () => {
        facebookSdkPromise = null;
        reject(new Error('Failed to load Facebook SDK.'));
      };
      document.body.appendChild(script);
    }
  });

  return facebookSdkPromise;
};

const formatConnectedDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const FacebookPageManager = ({ onClose }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [repairingPageId, setRepairingPageId] = useState('');

  const logApiError = (label, error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const message = detail || error?.message || 'Unknown error';
    const suffix = status ? `Status ${status}: ${message}` : message;
    console.error(label, suffix);
  };

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/facebook/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(response.data);
      const storedUserId =
        response.data?.find((page) => page?.facebook_user_id)?.facebook_user_id ||
        response.data?.find((page) => page?.facebook_user?.id)?.facebook_user?.id ||
        response.data?.find((page) => page?.user_id)?.user_id;
      if (storedUserId) {
        localStorage.setItem('facebook_user_id', storedUserId);
      }
      setError('');
    } catch (error) {
      logApiError('Error loading Facebook pages.', error);
      setError('Failed to load Facebook pages');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectPage = async () => {
    setError('');
    setSuccess('');

    if (isConnecting) {
      return;
    }

    const { protocol, hostname } = window.location;
    const isLocalhost =
      hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
    if (protocol !== 'https:' && !isLocalhost) {
      setError('Facebook Login requires HTTPS. Please use an https:// URL.');
      console.error('Facebook connect blocked: HTTPS is required for FB.login.');
      return;
    }

    setIsConnecting(true);

    try {
      const fb = await loadFacebookSdk();
      if (!fb || typeof fb.login !== 'function') {
        setError('Facebook SDK is not available.');
        console.error('Facebook SDK not available or missing FB.login.');
        setIsConnecting(false);
        return;
      }

      const finalizeConnect = async (accessToken, userID) => {
        localStorage.setItem('facebook_user_id', userID);
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/facebook/connect`,
          { user_access_token: accessToken },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const { facebook_pages_connected: pageCount = 0, instagram_accounts_connected: instagramCount = 0 } = response.data;
        setSuccess(
          `Connected ${pageCount} Facebook page${pageCount === 1 ? '' : 's'} with a durable server-side token.` +
          (instagramCount ? ` Refreshed ${instagramCount} linked Instagram account${instagramCount === 1 ? '' : 's'}.` : '')
        );
        await loadPages();
      };

      const handleLoginResponse = (response) => {
        const status = response?.status;
        if (!response) {
          setError('Popup blocked — allow popups and try again.');
          console.error('Facebook login popup was blocked or returned no response.');
          setIsConnecting(false);
          return;
        }

        if (status && status !== 'connected') {
          setError(status === 'not_authorized' ? 'Login not authorized.' : 'Login cancelled.');
          setIsConnecting(false);
          return;
        }

        if (!response.authResponse) {
          setError('Login cancelled.');
          setIsConnecting(false);
          return;
        }

        const { accessToken, userID } = response.authResponse;
        if (!accessToken || !userID) {
          setError('Login cancelled.');
          setIsConnecting(false);
          return;
        }

        finalizeConnect(accessToken, userID)
          .catch((error) => {
            logApiError('Failed to connect Facebook page.', error);
            setError(error.response?.data?.detail || 'Failed to connect Facebook page');
          })
          .finally(() => setIsConnecting(false));
      };

      fb.login(handleLoginResponse, {
        scope: 'pages_show_list,pages_read_engagement,pages_manage_engagement,pages_manage_metadata,pages_messaging,instagram_basic,instagram_manage_comments,instagram_manage_messages',
        return_scopes: true,
        auth_type: 'reauthorize'
      });
    } catch (error) {
      logApiError('Failed to load Facebook SDK.', error);
      setError(error.message || 'Failed to load Facebook SDK.');
      setIsConnecting(false);
    }
  };

  const handleToggleActive = async (pageId, currentStatus, pageName) => {
    const nextState = !currentStatus;
    const actionLabel = nextState ? 'activate' : 'deactivate';
    const confirmed = window.confirm(
      `Are you sure you want to ${actionLabel} "${pageName || pageId}"?`
    );
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/facebook/pages/${pageId}`,
        { is_active: nextState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Page ${pageName || pageId} ${nextState ? 'activated' : 'deactivated'}.`);
      loadPages();
    } catch (error) {
      logApiError('Error toggling page status.', error);
      setError('Failed to update page status');
    }
  };

  const handleRepairWebhookSubscription = async (page) => {
    const pageId = page?.page_id;
    if (!pageId || repairingPageId) return;

    setError('');
    setSuccess('');
    setRepairingPageId(pageId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/facebook/pages/${pageId}/webhook-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fields = response.data?.subscribed_fields?.join(', ') || 'required Page fields';
      setSuccess(`Meta accepted the ${fields} subscription for ${page.page_name || pageId}. New Messenger messages and post comments can now reach TickleGram.`);
    } catch (error) {
      logApiError('Error repairing Facebook webhook subscription.', error);
      setError(error.response?.data?.detail || 'Could not repair the Facebook webhook subscription. Reconnect the Page and try again.');
    } finally {
      setRepairingPageId('');
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this Facebook page?')) {
      return;
    }

    try {
      const page = pages.find((item) => item.page_id === pageId || item.id === pageId);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/facebook/pages/${page?.page_id || pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Facebook page deleted successfully!');
      loadPages();
    } catch (error) {
      if (error.response?.status === 404) {
        setSuccess('Facebook page deleted successfully!');
        loadPages();
        return;
      }
      logApiError('Error deleting Facebook page.', error);
      setError(error.response?.data?.detail || 'Failed to delete Facebook page');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="facebook-page-manager">
      <Card className="bg-[#1a1a2e] border-gray-800 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
                <FacebookIcon className="w-5 h-5 text-white" />
              </div>
              <CardTitle className="text-white text-xl">Manage Facebook Pages</CardTitle>
            </div>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <Alert className="bg-red-500/10 border-red-500/50 text-red-400">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="bg-green-500/10 border-green-500/50 text-green-400">
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {/* Add Page Button */}
          <Button
            onClick={handleConnectPage}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
            data-testid="add-page-button"
            disabled={isConnecting}
          >
            <Plus className="w-4 h-4 mr-2" />
            {isConnecting ? 'Connecting...' : 'Connect New Facebook Page'}
          </Button>

          {/* Pages List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Connected Pages</h3>
              <Button
                onClick={loadPages}
                size="sm"
                variant="outline"
                className="bg-transparent border-gray-700 text-gray-400 hover:text-white"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : pages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No Facebook pages connected yet
              </div>
            ) : (
              <div className="space-y-3">
                {pages.map((page) => (
                  <div
                    key={page.id}
                    className="bg-[#0f0f1a] border border-gray-700 rounded-lg p-4 flex items-center justify-between"
                    data-testid={`facebook-page-${page.page_id}`}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <FacebookIcon className="w-8 h-8 text-blue-500" />
                      <div>
                        <h4 className="text-white font-medium">
                          {page.page_name || `Page ${page.page_id.substring(0, 8)}...`}
                        </h4>
                        <p className="text-xs text-gray-400">ID: {page.page_id}</p>
                        <p className="text-xs text-gray-500">
                          Connected: {formatConnectedDate(page.connected_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <Button
                        onClick={() => handleRepairWebhookSubscription(page)}
                        size="sm"
                        variant="ghost"
                        disabled={!page.is_active || repairingPageId === page.page_id}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        title="Repair Messenger and post-comment webhook events"
                        aria-label={`Repair webhook for ${page.page_name || page.page_id}`}
                      >
                        <RefreshCw className={`w-4 h-4 ${repairingPageId === page.page_id ? 'animate-spin' : ''}`} />
                      </Button>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-400">Active</span>
                        <Switch
                          checked={page.is_active}
                          onCheckedChange={() => handleToggleActive(page.page_id, page.is_active, page.page_name)}
                          className="data-[state=checked]:bg-blue-600"
                        />
                      </div>
                      <Button
                        onClick={() => handleDeletePage(page.page_id)}
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FacebookPageManager;
